import { useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { formatCents, parseMoneyToCents, sanitizeMoneyInput } from "../../../../../utils/money";
import { AmountFigure, type AmountFigureSize } from "./AmountFigure";
import { BanknoteButton } from "./BanknoteButton";
import { BANKNOTES } from "./CashChangePanel";
import { CLOSE_FIELD_BOX, CLOSE_PHONE_FIELD_BOX } from "./closeLayout";
import { KeypadToggleButton } from "./GivenAmountField";
import { keypadTextOf, useKeypadOpen, type MoneyKeypadKey } from "./keypadState";
import { MoneyKeypad } from "./MoneyKeypad";

type MixedField = "cash" | "card";

/**
 * „Смесено", когато бакшишът е включен (дизайнът „Закрий"): „Общо за
 * плащане", под него „В брой" и „С карта" — редове-полета, като написаното в
 * едното допълва другото до сметката („остатък"); под тях „Банкноти в брой"
 * (добавят се към „В брой") или, при разпъната клавиатура, екранните цифри
 * (`MoneyKeypad`) — те пишат в последното избрано поле. При „Смесено"
 * бакшиш няма; картата е тук само за бакшиша, записан при закриването
 * (повторен опит). На телефон редовете са с размера на методите, без
 * екранна клавиатура.
 *
 * Без бакшиш в настройките модалът ползва стария `MixedPaymentPanel`.
 */
export function MixedTipPanel({
  dueCents,
  totalAmount,
  cashAmount,
  cardAmount,
  onCashChange,
  onCardChange,
  tipSlot,
  totalNote,
  figureSize,
  disabled = false,
}: {
  /** Дължимото — „Общо за плащане" (+ записания бакшиш при повторен опит). */
  dueCents: number;
  /** Поръчаното — разпределя се само то. */
  totalAmount: string;
  cashAmount: number;
  cardAmount: number;
  /** Родителят допълва другата половина до сметката. */
  onCashChange: (cash: number) => void;
  onCardChange: (card: number) => void;
  tipSlot: ReactNode;
  totalNote?: ReactNode;
  figureSize: AmountFigureSize;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const keypad = useKeypadOpen();
  const isPhone = figureSize === "phone";
  // На телефон екранна клавиатура няма — там е системната.
  const showsKeypad = keypad.isOpen && !isPhone;
  const totalCents = parseMoneyToCents(totalAmount) ?? 0;
  // Полето, в което пише екранната клавиатура.
  const [activeField, setActiveField] = useState<MixedField>("cash");
  // Каквото се пише в момента в едното поле (до излизане от него); другото
  // показва сумата си.
  const [editing, setEditing] = useState<{ field: MixedField; text: string } | null>(null);
  // Писаното от човека поле — другото е „остатък".
  const [lastEdited, setLastEdited] = useState<MixedField | null>(null);

  const centsOf = (field: MixedField) => parseMoneyToCents(field === "cash" ? cashAmount : cardAmount) ?? 0;
  // До сметката, не повече — като стария `InputNumber max`.
  const setFieldCents = (field: MixedField, cents: number) => {
    const cappedCents = Math.min(Math.max(cents, 0), totalCents);
    (field === "cash" ? onCashChange : onCardChange)(cappedCents / 100);
    setLastEdited(field);
  };
  const typeInto = (field: MixedField, raw: string) => {
    const text = sanitizeMoneyInput(raw);
    setEditing({ field, text });
    setFieldCents(field, parseMoneyToCents(text) ?? 0);
  };
  // Клавиш в поле, което не се пише в момента, започва отначало — като
  // фокус в полето (сумата се селектира и първият знак я заменя).
  const pressKeypadKey = (key: MoneyKeypadKey) => {
    const text = editing?.field === activeField ? editing.text : "";
    typeInto(activeField, keypadTextOf(text, key));
  };
  const textOf = (field: MixedField) => {
    if (editing?.field === field) return editing.text;
    const cents = centsOf(field);
    return cents > 0 ? formatCents(cents) : "";
  };
  const stopEditing = (field: MixedField) => {
    if (editing?.field === field) setEditing(null);
  };
  // Бутонът в поле: разпъната за друго поле — минава на това; иначе разпъва/свива.
  const toggleKeypadFor = (field: MixedField) => {
    // Курсорът в другото поле би писал там, а клавиатурата — тук; маха се.
    if (activeField !== field && document.activeElement instanceof HTMLInputElement) {
      document.activeElement.blur();
    }
    setActiveField(field);
    if (keypad.isOpen && activeField !== field) return;
    keypad.toggle();
  };

  const rowFor = (field: MixedField) => (
    <MixedAmountRow
      label={t(field === "cash" ? "closeTable.cash" : "closeTable.card")}
      isRemainder={lastEdited !== null && lastEdited !== field}
      value={textOf(field)}
      onChange={(raw) => typeInto(field, raw)}
      onFocus={() => setActiveField(field)}
      onNormalize={() => stopEditing(field)}
      isKeypadTarget={showsKeypad && activeField === field}
      keypadOpen={showsKeypad}
      onToggleKeypad={() => toggleKeypadFor(field)}
      isCompact={figureSize === "compact"}
      isPhone={isPhone}
      disabled={disabled}
    />
  );

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <AmountFigure
        label={t("closeTable.totalToPay")}
        cents={dueCents}
        size={figureSize}
        className="text-pos-accent"
      />
      {totalNote && (
        <div className="mt-1.5 text-[13px] font-medium leading-5 text-pos-soft2">{totalNote}</div>
      )}

      <div className="mt-5 flex flex-col gap-2.5">
        {rowFor("cash")}
        {rowFor("card")}
      </div>

      {showsKeypad ? (
        <div className="mt-3">
          <MoneyKeypad onKey={pressKeypadKey} disabled={disabled} />
        </div>
      ) : (
        <>
          <div className="mt-4 flex h-5 items-center text-[14px] font-bold text-pos-ink">
            {t("closeTable.banknotesCash")}
          </div>
          {/* На таблета и телефона пет „+100 €" на ред не се събират — по три. */}
          <div className={`mt-2.5 grid gap-2 ${figureSize === "large" ? "grid-cols-5" : "grid-cols-3"}`}>
            {BANKNOTES.map((banknote) => (
              <BanknoteButton
                key={banknote}
                banknote={banknote}
                disabled={disabled}
                onClick={() => {
                  setEditing(null);
                  setFieldCents("cash", centsOf("cash") + banknote * 100);
                }}
                className={isPhone ? "h-[43px] rounded-[10px] text-[15px]" : "h-12 rounded-xl text-[16px]"}
              />
            ))}
          </div>
        </>
      )}

      {/* empty:hidden — записан бакшиш има само при повторен опит */}
      <div className="mt-5 empty:hidden">{tipSlot}</div>
    </div>
  );
}

/** „В брой" / „С карта" — надписът (и „остатък") вляво, сумата вдясно, EUR и „Клавиатура". */
function MixedAmountRow({
  label,
  isRemainder,
  value,
  onChange,
  onFocus,
  onNormalize,
  isKeypadTarget,
  keypadOpen,
  onToggleKeypad,
  isCompact,
  isPhone,
  disabled,
}: {
  label: string;
  /** Допълнено до сметката от другото поле. */
  isRemainder: boolean;
  value: string;
  onChange: (raw: string) => void;
  onFocus: () => void;
  /** Излизане от полето / Enter — сумата се показва „x.xx". */
  onNormalize: () => void;
  /** Екранната клавиатура пише тук. */
  isKeypadTarget: boolean;
  keypadOpen: boolean;
  onToggleKeypad: () => void;
  isCompact: boolean;
  /** Телефон — с размера на методите, без бутона за клавиатура. */
  isPhone: boolean;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const inputId = useId();
  return (
    <label
      htmlFor={inputId}
      className={`flex cursor-text items-center gap-3 border bg-pos-card ${isPhone ? CLOSE_PHONE_FIELD_BOX : CLOSE_FIELD_BOX} transition-[border-color,box-shadow] focus-within:border-pos-accent focus-within:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-pos-accent)_14%,transparent)] ${
        isKeypadTarget
          ? "border-pos-accent shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-pos-accent)_14%,transparent)]"
          : "border-pos-line"
      }`}
    >
      {/* На таблета „остатък" е под надписа — иначе редът не се събира. */}
      <span
        className={`flex min-w-0 shrink-0 ${isCompact ? "flex-col" : isPhone ? "items-baseline gap-3" : "items-baseline gap-[30px]"}`}
      >
        <span className={`font-bold text-pos-ink ${isCompact ? "text-[13px]" : isPhone ? "text-[13.5px]" : "text-[14px]"}`}>
          {label}
        </span>
        {isRemainder && (
          <span className={`font-medium text-pos-muted ${isCompact ? "text-[11px]" : "text-[13px]"}`}>
            {t("closeTable.remainder")}
          </span>
        )}
      </span>
      <input
        id={inputId}
        // Само надписът — иначе редът-label дава и „остатък EUR Клавиатура" в името.
        aria-label={label}
        value={value}
        placeholder="0.00"
        disabled={disabled}
        // Разпъната клавиатура — системната не изскача на тъч екран.
        inputMode={keypadOpen ? "none" : "decimal"}
        autoComplete="off"
        onChange={(event) => onChange(event.target.value)}
        onFocus={(event) => {
          onFocus();
          event.target.select();
        }}
        onBlur={onNormalize}
        onKeyDown={(event) => {
          // Enter само нормализира — закриването е с бутона.
          if (event.key === "Enter") {
            event.preventDefault();
            onNormalize();
          }
        }}
        // Телефон — 16px: под 16 iOS зуумва страницата при фокус.
        className={`h-full w-full min-w-0 flex-1 border-0 bg-transparent p-0 text-right font-pos-mono font-bold text-pos-ink outline-none [font-variant-numeric:tabular-nums] placeholder:text-pos-faint disabled:cursor-not-allowed ${
          isPhone ? "text-[16px]" : "text-[22px]"
        }`}
      />
      <span className={`shrink-0 font-bold text-pos-muted ${isPhone ? "text-[13px]" : "text-[14px]"}`}>EUR</span>
      {!isPhone && (
        <>
          <span aria-hidden className="h-7 w-px shrink-0 bg-pos-line2" />
          <KeypadToggleButton isOpen={isKeypadTarget} onToggle={onToggleKeypad} disabled={disabled} />
        </>
      )}
    </label>
  );
}
